import { cn } from "@/lib/utils.ts";
import type { ReactNode } from "react";

/**
 * A capped, vertically scrolling region whose scrollbar is drawn over the
 * content rather than beside it.
 *
 * A native scrollbar — styled or not — takes real layout space, which is what
 * held every sticky section band off the right edge of these cards: the band
 * stopped where the gutter began, leaving a notch of card showing through it.
 * Reserving no space lets a band run the full width, and lets the region sit
 * flush against the card's own edges, while the thumb keeps its own inset from
 * the corner radius so its rounded tips are never masked.
 *
 * Nothing measures the thumb. Its travel comes from a scroll-driven animation and
 * its visibility from that timeline going inactive when there is nothing to
 * scroll — see `.omni-scroll` in `index.css`, which is where the arithmetic that
 * used to live here now is.
 *
 * The thumb is a readout rather than a control, but it still takes the press
 * rather than passing it through: at two call sites it renders inside the button
 * that opens the artifact, and a grab at something that looks like a scrollbar
 * must not navigate. The cost is that the strip it occupies no longer pans the
 * region under touch — `touch-none` is what stops the browser claiming the
 * gesture, and the thumb is a sibling of the scrolling box rather than a child,
 * so the gesture has no scroller to fall through to.
 *
 * `className` belongs to the scrolling box, so its height cap goes there. Keep
 * margins and borders out of it: the thumb is positioned against the wrapper,
 * and anything that offsets the box from the wrapper offsets the track from the
 * content it represents.
 */
export const OmniScrollArea = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div className="omni-scroll relative">
    <div className={cn("omni-scroll-viewport overflow-y-auto overflow-x-hidden", className)}>
      {children}
    </div>
    <div
      aria-hidden
      // `preventDefault` on a press does not suppress the click that follows it,
      // so stopping the click is what keeps a grab of the bar from opening the
      // artifact underneath.
      onClick={(event) => event.stopPropagation()}
      // Above the sticky section bands, which carry `z-10` of their own: the bar
      // overlays the content rather than sitting beside it, so anything that
      // outranked it would swallow the thumb as it passed. `cursor-default`
      // because the bar reports position and does not move it — inheriting the
      // enclosing button's pointer cursor would promise an action it has not got.
      className="omni-scroll-thumb absolute right-0.5 z-20 h-6 w-1.5 cursor-default touch-none select-none rounded-full bg-border"
    />
  </div>
);
